import { useEffect, useRef } from 'react'
import { Screen, PrimaryButton } from './Screen.jsx'
import { ErrorBoundary, CrashHeading } from './ErrorBoundary.jsx'
import { currentBundle, screensOf } from '../config.js'

/**
 * A crash on an ANSWERING SURFACE must not cost the participant her sitting.
 *
 * THE DEFECT THIS EXISTS FOR. Design hit `ReferenceError: useState is not
 * defined` from a half-saved OutstandingNote while driving DS-14. That throw
 * was transient and theirs to ignore; the absence of a boundary was not. Any
 * component throw white-screened the entire day-0 battery — no message, no
 * route back, and no way to know whether anything had been kept.
 *
 * Her answers ARE kept. Every item upserts on its own the moment it is chosen,
 * so a crash 150 items into a 200-item sitting loses at most the item on
 * screen. But a blank page after two hours of disclosing tells her the
 * opposite, and she has no way to find out otherwise.
 *
 * TWO VARIANTS, AND THE DIFFERENCE IS TRUTH RATHER THAN TONE (DS-2 §3.11a).
 * Day 0 is a resumable task, so its copy may say she can carry on from where
 * she stopped. A BEEP EXPIRES 60 MINUTES AFTER FIRING, so by the time she
 * reloads that promise can already be false. The beep variant therefore states
 * only that what she has already given is saved, and never that the check-in is
 * still there. Same defect class as everything else in this project: a claim
 * whose scope is wider than what stands behind it.
 *
 * WHY currentBundle() AND NOT useConfigBundle(). This renders when something
 * has already gone wrong, possibly config loading itself. The cached bundle is
 * synchronous and cannot suspend, refetch, or throw a second time inside a
 * crash screen. The inlined defaults are config's exact strings, so a cold
 * cache degrades to the same words rather than to silence.
 */
function BatteryCrashScreen({ variant }) {
  const heading = useRef(null)
  useEffect(() => {
    heading.current?.focus()
  }, [])

  const fallback = DEFAULTS[variant] ?? DEFAULTS.day0
  const copy = screensOf(currentBundle())?.error_boundary?.[variant] ?? fallback

  /*
   * NOT --critical. Normal surface, --ink-900 heading, --ink-700 body. A red
   * screen reads as alarm and this is a nuisance with a known fix, not an
   * emergency. No illustration, no icon, no spinner.
   *
   * NOT role="alert": this screen has replaced everything, and an alert
   * competes with the heading for announcement. Focus moves to the h1 instead.
   *
   * The support affordance renders via Screen, OUTSIDE the crashing subtree. A
   * boundary firing over a safety surface would otherwise replace crisis
   * resources with an error message for someone who has just disclosed — the
   * worst reachable state in the app, prevented by placement, not by copy.
   */
  return (
    <Screen support>
      <CrashHeading headingRef={heading}>{copy.heading ?? fallback.heading}</CrashHeading>
      <p className="text-base leading-relaxed text-[var(--ink-900)]">
        {copy.body ?? fallback.body}
      </p>
      <div className="mt-8">
        {/*
          NO AUTO-RELOAD. This fires on her tap and never on mount: an automatic
          reload of a crashing subtree is a loop she cannot escape.
        */}
        <PrimaryButton onClick={() => window.location.reload()}>
          {copy.action ?? fallback.action}
        </PrimaryButton>
      </div>
    </Screen>
  )
}

/**
 * config's exact strings (CFG-30 screens.error_boundary), inlined ONLY as the
 * cold-cache fallback. If these ever diverge from screens.json, screens.json
 * wins: config owns the wording and this file owns nothing but the mechanism.
 */
const DEFAULTS = {
  day0: {
    heading: 'This screen stopped working.',
    body: 'Your answers are saved. They are stored one at a time, as you give them, so at most the question you were on may need answering again.',
    action: 'Carry on',
  },
  beep: {
    heading: 'This check-in stopped working.',
    body: 'The answers you have already given are saved. They are stored one at a time, as you give them.',
    action: 'Open the check-in',
  },
}

/**
 * Shares ErrorBoundary's mechanism and none of RootBoundary's words. The
 * sentence about saved answers is true HERE and only here, which is why the
 * copy lives on this side and never in the shared class.
 *
 * @param {'day0'|'beep'} variant which answering surface is underneath
 */
export function BatteryBoundary({ children, variant = 'day0' }) {
  return (
    <ErrorBoundary label={`battery:${variant}`} fallback={<BatteryCrashScreen variant={variant} />}>
      {children}
    </ErrorBoundary>
  )
}
